import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import { DashboardService } from '../home/dashboard.service';
import { TenantInfo } from '../model/dashboard.model';

/**
 * Onboarding status the backend reports once all tenant data is loaded.
 * Other known statuses are 'new', 'Started' and 'NoDB' (tenant has no database yet).
 */
const COMPLETED_ONBOARDING_STATUS = 'completed';

/** The check runs in a route guard, so a hanging backend must not block navigation for long. */
const TENANT_INFO_TIMEOUT_MS = 10 * 1000;

/** A failed check is retried after this delay instead of on every navigation. */
const RETRY_FAILED_CHECK_AFTER_MS = 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class TenantOnboardingService {
  private readonly dashboardService = inject(DashboardService);
  private readonly _isOnboarded = signal<boolean | null>(null);
  private readonly _checkFailedAt = signal(0);

  /** Whether the tenant of the current context completed onboarding; null until loaded. */
  readonly isOnboarded = this._isOnboarded.asReadonly();

  /** True once the module may be offered: onboarding completed, or the check failed and counts as onboarded. */
  readonly isModuleAvailable = computed(() => this._isOnboarded() === true || this._checkFailedAt() > 0);

  /**
   * Resolves whether the tenant of the current context completed onboarding.
   * The result is cached for the session. A failed or timed out request counts as onboarded,
   * so a temporary outage does not show the onboarding page to boarded tenants; it is not
   * cached and the check is retried after a short delay.
   */
  checkOnboarded(): Observable<boolean> {
    const cached = this._isOnboarded();
    if (cached !== null) {
      return of(cached);
    }
    if (Date.now() - this._checkFailedAt() < RETRY_FAILED_CHECK_AFTER_MS) {
      return of(true);
    }

    return this.dashboardService.getTenantInfo().pipe(
      timeout(TENANT_INFO_TIMEOUT_MS),
      map((tenants) => isOnboarded(tenants)),
      tap((onboarded) => {
        this._isOnboarded.set(onboarded);
        this._checkFailedAt.set(0);
      }),
      catchError(() => {
        this._checkFailedAt.set(Date.now());
        return of(true);
      }),
    );
  }

  clear(): void {
    this._isOnboarded.set(null);
    this._checkFailedAt.set(0);
  }
}

/** Onboarded only when tenant info is returned and every tenant in the context completed onboarding. */
function isOnboarded(tenants: TenantInfo[]): boolean {
  return (
    tenants.length > 0 &&
    tenants.every((tenant) => tenant.onboardingStatus?.toLowerCase() === COMPLETED_ONBOARDING_STATUS)
  );
}
